import { useEffect, useRef, useState } from "react";
import { Lock, Check, Loader2, Plus, Trash2 } from "@/lib/icons";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SHOW = 5;
const TEMPLATES = {
  blank: { label: "Blank note", text: "" },
  prep: { label: "Session prep", text: "GOALS REVIEWED\n- \n\nWINS SINCE LAST TIME\n- \n\nBLOCKERS / WHAT GOT IN THE WAY\n- \n\nWATCHING FOR (scales, patterns)\n- \n\nPLAN FOR THIS SESSION\n- " },
  recap: { label: "Session recap", text: "WHAT WE COVERED\n- \n\nCLIENT'S OWN WORDS\n- \n\nAGREED NEXT STEPS\n- \n\nHABIT / REWARD TWEAKS\n- \n\nFOLLOW UP NEXT TIME\n- " },
  checkin: { label: "Quick check-in", text: "MOOD / ENERGY TODAY\n- \n\nONE THING THAT WENT WELL\n- \n\nONE THING TO TRY\n- " },
};
const fmtDate = (d) => new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);

function NoteEntry({ entry, onSaved, onDelete }) {
  const [text, setText] = useState(entry.text);
  const [date, setDate] = useState(entry.date);
  const [state, setState] = useState("idle");
  const ref = useRef(null);
  const dirty = text !== entry.text || date !== entry.date;
  useEffect(() => { if (ref.current) { ref.current.style.height = "auto"; ref.current.style.height = `${Math.min(ref.current.scrollHeight, 360)}px`; } }, [text]);
  const save = async () => {
    if (!dirty) return;
    setState("saving");
    try { const r = await api.put(`/clinician/notes/${entry.id}`, { text, date }); onSaved(r.data); setState("idle"); }
    catch { setState("idle"); toast.error("Couldn't save this note — click back into it to retry."); }
  };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3" data-testid={`note-entry-${entry.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        <label className="text-xs font-bold text-stone-800 inline-flex items-center gap-2">
          <span data-testid={`note-date-label-${entry.id}`}>{fmtDate(date)}</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={save} data-testid={`note-date-${entry.id}`}
            className="text-[11px] font-normal text-stone-500 bg-transparent border border-stone-200 rounded-md px-1.5 py-0.5 outline-none focus:border-amber-600" />
        </label>
        <div className="inline-flex items-center gap-3 text-[11px] text-stone-500">
          <span data-testid={`note-status-${entry.id}`} className="inline-flex items-center gap-1">
            {state === "saving" ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : dirty ? <span className="text-amber-700">Unsaved — saves when you click away</span> : <><Check className="w-3 h-3 text-emerald-700" /> Saved {fmtTime(entry.updated_at)}</>}
          </span>
          <button onClick={() => onDelete(entry)} data-testid={`note-delete-${entry.id}`} className="text-stone-400 hover:text-red-700" title="Delete this note"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <textarea ref={ref} value={text} onChange={(e) => setText(e.target.value)} onBlur={save} rows={1} data-testid={`note-text-${entry.id}`} autoFocus={!entry.text}
        placeholder="Prep notes, things to ask about, what to watch for next session…"
        className="w-full resize-none rounded-xl bg-transparent px-1 py-1 text-sm text-stone-800 outline-none leading-relaxed" />
    </div>
  );
}

export function SessionNotes({ clientId }) {
  const [entries, setEntries] = useState([]);
  const [all, setAll] = useState(false);
  const [tpl, setTpl] = useState("blank");
  useEffect(() => { setEntries([]); setAll(false); api.get(`/clinician/client/${clientId}/notes`).then((r) => setEntries(r.data.entries)); }, [clientId]);

  const add = async () => {
    const r = await api.post(`/clinician/client/${clientId}/notes`, { text: TEMPLATES[tpl].text });
    setEntries((es) => [r.data, ...es]);
  };
  const saved = (e) => setEntries((es) => es.map((x) => (x.id === e.id ? e : x)).sort((a, b) => (b.date + b.created_at).localeCompare(a.date + a.created_at)));
  const del = async (e) => {
    if (e.text && !window.confirm(`Delete the note from ${fmtDate(e.date)}? This can't be undone.`)) return;
    await api.delete(`/clinician/notes/${e.id}`);
    setEntries((es) => es.filter((x) => x.id !== e.id));
  };
  const shown = all ? entries : entries.slice(0, SHOW);

  return (
    <section className="warm-card p-5" data-testid="session-notes">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-700" />
          <h3 className="font-display text-base font-bold text-stone-900">Session notes</h3>
          <span className="text-[10px] font-bold uppercase tracking-widest text-stone-500">Private · encrypted · never shown to the member</span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <select value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="session-notes-template" className="text-xs font-semibold rounded-full border border-stone-300 bg-white px-2.5 py-1.5 text-stone-700 outline-none focus:border-amber-600">
            {Object.entries(TEMPLATES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
          </select>
          <button onClick={add} data-testid="session-notes-add" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700"><Plus className="w-3.5 h-3.5" /> Add note</button>
        </div>
      </div>
      {entries.length === 0 && <p className="text-sm text-stone-500" data-testid="session-notes-empty">No notes yet. Add one before or after a session — each entry is dated so you can scroll back over months.</p>}
      <div className="space-y-2">{shown.map((e) => <NoteEntry key={e.id} entry={e} onSaved={saved} onDelete={del} />)}</div>
      {entries.length > SHOW && (
        <button onClick={() => setAll((v) => !v)} data-testid="session-notes-toggle-all" className="mt-3 text-xs font-semibold text-amber-800 hover:text-amber-900">
          {all ? "Show the latest 5" : `Show all ${entries.length} notes`}
        </button>
      )}
    </section>
  );
}
